import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../util/axios";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import { AddDeviceFormValues } from "../types/device";
import { Device } from "../components/DeviceTable/DeviceTable.types";
import { useTranslation } from "react-i18next";

export const useGetDevices = () =>
  useQuery<Device[]>({
    queryKey: ["devicesList"],
    queryFn: () => axiosInstance.get("/device/overview").then((w) => w.data),
    initialData: [],
  });

export const useAddDevice = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: AddDeviceFormValues) =>
      axiosInstance.post(`/device/connect`, data),
    onSuccess: () => {
      notification.success({
        message: t("toast.device-connected"),
        description: t("toast.device-connected-description"),
      });
      navigate("/portal/devices");
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-connect-device"),
        description: t("toast.please-check-your-credentials"),
      });
    },
  });
};

export const useDeleteDevice = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) =>
      axiosInstance.delete(`/device/${deviceId}`),
    onSuccess: () => {
      notification.success({
        message: t("toast.device-removed"),
        description: t("toast.device-removed-description"),
      });
      return queryClient.invalidateQueries({ queryKey: ["devicesList"] });
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-remove-device"),
      });
    },
  });
};

export const useConnectResource = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { deviceId: string; resourceId: string }) =>
      axiosInstance.post(`/device/connect-resource`, data),
    onSuccess: (_data, variables) => {
      if (variables.resourceId === "") {
        notification.success({
          message: t("toast.resource-disconnected"),
          description: t("toast.resource-disconnected-description"),
        });
        return queryClient.invalidateQueries({ queryKey: ["devicesList"] });
      }
      notification.success({
        message: t("toast.resource-connected"),
        description: t("toast.resource-connected-description"),
      });
      return queryClient.invalidateQueries({ queryKey: ["devicesList"] });
    },
    onError: () => {
      notification.error({
        message: t("toast.failed-to-(dis)connect-resource"),
      });
    },
  });
};
